import { useEffect, useMemo, useState } from 'react';
import { subMonths } from 'date-fns';
import { useAuth } from '../services/authentication/authContext';
import { useRestaurantStore } from '../stores/appRestaurantStore';
import { enrichOrders } from '../stores/appOrderHistoryStore';
import { enrichRevenue } from '../stores/appRevenueHistoryStore';
import { fetchOrderHistoryByPlatformIds, fetchOrderHistoryDateBounds } from '../services/orderhistory/orderhistoryService';
import { fetchRevenueHistoryByRevenueSourceIds, fetchRevenueHistoryDateBounds } from '../services/revenuehistory/revenueHistoryService';
import { fetchCostPriceAnalysesByRestaurant } from '../services/costPrice/costPriceService';
import { fetchSubrecipesByRestaurant } from '../services/subrecipes/subrecipesService';
import { computeUtilizationReport, UtilizationReportInputs } from '../utils/reports/utilizationReport';
import { mergeOrganizationUtilization } from '../utils/reports/organizationUtilization';
import { ReportRange } from '../utils/reports/shared';
import { resolveWastePercentage } from '../utils/wasteUtils';
import { isPracticeId } from '../components/onboarding/practice/practiceGuards';

type RestaurantInputs = Omit<UtilizationReportInputs, 'range' | 'defaultWastePercentage'>;
interface LoadState {
  context: string;
  loading: boolean;
  inputs: Record<string, RestaurantInputs>;
  failed: string[];
}

/** Bound large history responses just like the progressive history stores. Adjacent
 * windows include their shared boundary, so merge by id before computing totals. */
async function fetchBoundedHistory<T extends { id: string }>(
  ids: string[],
  fetchBounds: (ids: string[]) => Promise<{ minDate: Date | null; maxDate: Date | null }>,
  fetchChunk: (ids: string[], start: Date, end: Date) => Promise<T[]>,
  isCancelled: () => boolean,
): Promise<T[]> {
  if (!ids.length || isCancelled()) return [];
  const { minDate, maxDate } = await fetchBounds(ids);
  if (isCancelled() || (!minDate && !maxDate)) return [];
  if (!minDate || !maxDate || !Number.isFinite(minDate.getTime()) || !Number.isFinite(maxDate.getTime()) || minDate > maxDate) {
    throw new Error('Invalid organization utilization history bounds');
  }
  const rows = new Map<string, T>();
  let end = maxDate;
  while (!isCancelled()) {
    const candidate = subMonths(end, 6);
    const start = candidate < minDate ? minDate : candidate;
    const chunk = await fetchChunk(ids, start, end);
    if (isCancelled()) return [];
    for (const row of chunk) rows.set(row.id, row);
    if (start.getTime() === minDate.getTime()) break;
    end = start;
  }
  return Array.from(rows.values());
}

/** Isolated read-only inputs: never replace the selected restaurant's editing stores.
 * Load at most four restaurants concurrently, and reuse inputs when dates change.
 * Returning from an individual restaurant remounts this hook and refreshes recipes.
 */
export function useOrganizationUtilization(range: ReportRange) {
  const { backendUser } = useAuth();
  const hasBackendUser = !!backendUser;
  const restaurants = useRestaurantStore(s => s.restaurants);
  const ready = useRestaurantStore(s => s.isDataLoaded);
  const sections = useRestaurantStore(s => s.platformToSectionIndex);
  const sources = useRestaurantStore(s => s.restaurantRevenueSources);
  const settings = useRestaurantStore(s => s.reportSettings);
  const [attempt, setAttempt] = useState(0);
  const context = JSON.stringify([backendUser?.id, backendUser?.created_by, restaurants.map(r => r.id)]);
  const [state, setState] = useState<LoadState>({ context: '', loading: true, inputs: {}, failed: [] });

  useEffect(() => {
    if (!ready || !hasBackendUser) return;
    let cancelled = false;
    const isCancelled = () => cancelled;
    let cursor = 0;
    const inputs: LoadState['inputs'] = {};
    const failed: string[] = [];
    setState({ context, loading: true, inputs: {}, failed: [] });
    const worker = async () => {
      while (!cancelled && cursor < restaurants.length) {
        const restaurant = restaurants[cursor++];
        try {
          const platformIds = Object.entries(sections).filter(([, section]) => section.restaurantId === restaurant.id).map(([id]) => id);
          const sourceIds = (sources[restaurant.id] ?? []).map(source => source.id);
          const orders = await fetchBoundedHistory(platformIds, fetchOrderHistoryDateBounds, fetchOrderHistoryByPlatformIds, isCancelled);
          if (cancelled) return;
          const revenue = await fetchBoundedHistory(sourceIds, fetchRevenueHistoryDateBounds, fetchRevenueHistoryByRevenueSourceIds, isCancelled);
          if (cancelled) return;
          const [costPriceList, subrecipes] = await Promise.all([
            fetchCostPriceAnalysesByRestaurant(restaurant.id),
            fetchSubrecipesByRestaurant(restaurant.id),
          ]);
          if (cancelled) return;
          inputs[restaurant.id] = {
            currentRestaurantId: restaurant.id,
            orders: enrichOrders(orders, sections),
            revenue: enrichRevenue(revenue, sources),
            costPriceList: costPriceList.filter(d => !isPracticeId(d.id)),
            subrecipesById: new Map(subrecipes.filter(s => !isPracticeId(s.id)).map(s => [s.id, s])),
          };
        } catch (error) {
          if (cancelled) return;
          console.error('Failed to load organization utilization restaurant', restaurant.id, error);
          failed.push(restaurant.id);
        }
      }
    };
    void Promise.all(Array.from({ length: Math.min(4, restaurants.length) }, worker)).then(() => {
      if (!cancelled) setState({ context, loading: false, inputs, failed });
    });
    return () => { cancelled = true; };
  }, [ready, hasBackendUser, context, restaurants, sections, sources, attempt]);

  const current = state.context === context;
  const reports = useMemo(() => !current ? [] : restaurants.flatMap(restaurant => {
    const inputs = state.inputs[restaurant.id];
    if (!inputs) return [];
    return [{
      restaurantId: restaurant.id,
      restaurantName: restaurant.name,
      data: computeUtilizationReport({
        ...inputs, range,
        defaultWastePercentage: resolveWastePercentage(settings[restaurant.id]?.wastePercentage),
      }),
    }];
  }), [current, state.inputs, restaurants, range, settings]);
  const data = useMemo(() => mergeOrganizationUtilization(reports), [reports]);
  return {
    data,
    reports,
    inputs: current ? state.inputs : {},
    loading: !ready || !current || state.loading,
    failed: current ? restaurants.filter(r => state.failed.includes(r.id)) : [],
    missingSetup: reports.filter(r => !r.data.hasMappableCostPrices),
    retry: () => setAttempt(n => n + 1),
  };
}
